import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { Divider, Flex } from "@theorgchartdev/storybook";
import TextFormatToolsComposite from "@/appComponents/Navigation/ContextualToolbar/TextFormatToolsComposite/TextFormatToolsComposite";
import { PhotoCellToolsComposite } from "@/appComponents/Navigation/ContextualToolbar/PhotoCellToolsComposite/PhotoCellToolsComposite";
import {
  kLayoutToolbarOffset,
  LayoutToolbar
} from "@/appComponents/Blocks/Composites/LayoutHoverToolbar/LayoutToolbar";
import { InsertRowMenu } from "@/appComponents/Blocks/Composites/LayoutHoverToolbar/InsertRowMenu";
import { InsertColumnMenu } from "@/appComponents/Blocks/Composites/LayoutHoverToolbar/InsertColumnMenu";
import { MergeCellMenu } from "@/appComponents/Blocks/Composites/LayoutHoverToolbar/MergeCellMenu";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumChartElement } from "@coreapp/constants";
import { CenterIt } from "@coreapp/utils/CenterIt";

const useCenterBoxPositioning = (boxRef, floatingCtrlRef, dependencies) => {
  const [positionReady, setPositionReady] = useState(false);
  useEffect(() => {
    const handlePositioning = () => {
      const wsMgr = GetWSMgr();
      const element = document.getElementById("basecanvas");
      if (!element || !boxRef.current) return false;

      const rect = element.getBoundingClientRect();
      const topOffset = rect.top + window.scrollY;
      const leftOffset = rect.left + window.scrollX;

      const parent = element.offsetParent;
      let parentLeftOffset = 0;
      let parentTopOffset = 0;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        parentLeftOffset = parentRect.left + window.scrollX;
        parentTopOffset = parentRect.top + window.scrollY;
      }

      const parentPanel = wsMgr?.GetCurrentPanel();
      if (!parentPanel) return false;

      const offsetY = parentPanel.GetPositionY();
      const offsetX = parentPanel.GetPositionX();
      const zoomFactor = parentPanel.GetScaleFactor();
      const panX = parentPanel.GetPanX();
      const panY = parentPanel.GetPanY();

      const box = boxRef.current;
      const elementX = box.GetPositionX() + offsetX;
      const elementY = (box.GetPositionY() - panY) * zoomFactor + offsetY;
      const boxHeight = box.GetHeight() * zoomFactor;
      const boxHangDelta = box.GetBorderOutset() * 2 * zoomFactor;
      const layout = box.GetLayout();
      const bgMgr = wsMgr.fBackgroundMgr;
      const area = bgMgr.DetermineLayoutArea(layout);
      const flipAxis = area.includes("bottom");

      const totalHangDelta = boxHangDelta + layout.SCBreakBottomAdder() * zoomFactor;
      const columnResizerY = wsMgr?.fLayoutPanel?.fColumnResizer?.GetPositionY() ?? 0;
      const resizerOffset =
        Math.max(0, columnResizerY - (box.GetPositionY() + box.GetHeight())) * zoomFactor;
      const boxOffset = boxHeight + totalHangDelta;
      const toolbarElement = document.querySelector(".ble-toolbar");
      const interactWidth = toolbarElement?.offsetWidth ?? 600;
      const interactHeight = toolbarElement?.offsetHeight ?? 0;
      let yPos = elementY;
      if (flipAxis) yPos -= totalHangDelta + interactHeight + 2 * kLayoutToolbarOffset;
      else yPos += boxOffset + resizerOffset;
      const hoverElementWidth = box.GetWidth() * zoomFactor;
      const shiftDelta = CenterIt(hoverElementWidth, interactWidth);
      const xPos = (elementX - panX) * zoomFactor + shiftDelta;

      floatingCtrlRef.current?.setPositionReference({
        clientX: xPos + leftOffset - parentLeftOffset,
        clientY: yPos + topOffset - parentTopOffset
      });

      return true;
    };

    const positionHandled = handlePositioning();
    setPositionReady(positionHandled === true);
    window.addEventListener("resize", handlePositioning);
    return () => window.removeEventListener("resize", handlePositioning);
  }, [boxRef, floatingCtrlRef, ...dependencies]);

  return { positionReady };
};

export const LayoutHoverToolbar = props => {
  if (!Globals._uiConfig.fBLEHoverBarEnabled) return;

  const { targetBox, targetCell } = props;
  const openCtrlRef = useRef(null);
  const floatingCtrlRef = useRef(null);
  const boxRef = useRef(null);
  const cellRef = useRef(null);

  boxRef.current = targetBox;
  cellRef.current = targetCell;

  useImperativeHandle(props.openCtrlRef, () => openCtrlRef.current);
  useImperativeHandle(props.floatingCtrlRef, () => floatingCtrlRef.current);

  const renderControls =
    targetCell && targetCell.IsSelected() && !targetCell?.GetCellDesc()?.IsGutterCell();

  useEffect(() => {
    if (!renderControls) {
      openCtrlRef?.current?.setOpen(false);
    }
  }, [renderControls]);

  const { positionReady } = useCenterBoxPositioning(boxRef, floatingCtrlRef, [
    targetBox,
    targetCell
  ]);

  const isSelectionMerged = () => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) return false;

    const firstCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    if (!firstCell) return false;

    const cellDesc = firstCell.GetCellDesc();
    return cellDesc.IsMergedCell();
  };

  const onClose = () => {
    if (targetCell && targetCell.IsSelected()) {
      openCtrlRef?.current?.setOpen(true);
    }
  };

  const isMerged = isSelectionMerged();
  const dividerClass = "ocn-toolbar-horizontal-divider";
  const inBackgroundEditMode = Globals._uiConfig.InBackgroundEditorMode();
  const modePrefixId = plainPrefix =>
    inBackgroundEditMode ? `background-editor-${plainPrefix}` : plainPrefix;
  const doc = GetDoc();

  if (!doc) {
    return null;
  }

  const hidden = !positionReady || !renderControls;
  const isPositionDoc = doc.IsPositionDocument();

  return (
    <LayoutToolbar
      popoverProps={{
        onClose: onClose,
        floatingProps: {
          portalProps: { containerId: "modal-root" }
        }
      }}
      floatingCtrlRef={floatingCtrlRef}
      openCtrlRef={openCtrlRef}
      overlayProps={{
        className: `ble-toolbar ${hidden ? "hidden" : ""}`
      }}
    >
      {renderControls && (
        <Flex gap={"12px"} padding={"8px 16px"} alignItems={"center"} width={"100%"}>
          <Flex gap={"12px"} justifyContent={"space-between"}>
            <TextFormatToolsComposite
              useSelect
              showFieldDisplay
              hideTextControls
              menuProps={{
                customClass: "oc-toolbar-menu",
                optionSize: "small"
              }}
              enableMultiFieldMenu={isPositionDoc && inBackgroundEditMode}
              dataTestIdPrefix={modePrefixId("ctx-toolbar-ble")}
            />
          </Flex>
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <InsertRowMenu dataTestIdPrefix={modePrefixId("layout-ht")} />
          <InsertColumnMenu dataTestIdPrefix={modePrefixId("layout-ht")} />
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <MergeCellMenu isMerged={isMerged} dataTestIdPrefix={modePrefixId("layout-ht")} />
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <PhotoCellToolsComposite dataTestIdPrefix={modePrefixId("layout-ht-")} />
        </Flex>
      )}
    </LayoutToolbar>
  );
};
